import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../context/WishlistContext";
import { api, inr } from "../../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Package, Sparkles, ArrowRight, Heart, MapPin, ChevronRight, Trash2, Star, FileDown } from "lucide-react";
import WishlistShare from "../../components/store/WishlistShare";

const STATUS_STYLE = {
  delivered: {bg:"#E8EDDF", color:"#2E4A35", label:"Delivered"},
  shipped: {bg:"#FEF3E2", color:"#8B7355", label:"Shipped"},
  packed: {bg:"#FEF3E2", color:"#8B7355", label:"Packed"},
  confirmed: {bg:"#F5F0E6", color:"#3B2F2F", label:"Confirmed"},
  out_for_delivery: {bg:"#F5DEC7", color:"#993322", label:"Out for delivery"},
  cancelled: {bg:"#F5DEC7", color:"#993322", label:"Cancelled"},
  returned: {bg:"#F5DEC7", color:"#993322", label:"Returned"},
};

export default function Account() {
  const { user, loading, login, logout } = useAuth();
  const { toggle: toggleWish } = useWishlist();
  const [orders, setOrders] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (user) {
      api.get("/orders/my").then(r=>setOrders(r.data)).catch(()=>{});
      api.get("/wishlist/products").then(r=>setWishProducts(r.data)).catch(()=>{});
    }
  }, [user]);

  if (loading) return <div className="py-16 text-center text-stone-500">Loading…</div>;
  if (!user) return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 40, right: -180 }}/>
      <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: 100, left: -140 }}/>
      <div className="max-w-md mx-auto px-4 py-24 text-center relative">
        <div className="w-20 h-20 rounded-full bg-white shadow-xl flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-9 h-9 text-[#D4AF37]"/>
        </div>
        <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">Welcome back</div>
        <h1 className="font-serif-display text-5xl mb-4">Sign in to your account</h1>
        <p className="text-stone-600 mb-8 leading-relaxed">Track orders, save your addresses, keep a wishlist of favorites, and reorder in one tap.</p>
        <button onClick={login} data-testid="account-login-btn"
                className="bg-[#3B2F2F] text-white rounded-full px-7 py-3.5 hover:bg-black shadow-lg shadow-[#3B2F2F]/20">
          Sign in with Google
        </button>
        <div className="mt-8 grid grid-cols-3 gap-2 text-xs text-stone-500">
          <div className="flex flex-col items-center gap-1"><Package className="w-4 h-4 text-[#D4AF37]"/>Track orders</div>
          <div className="flex flex-col items-center gap-1"><Heart className="w-4 h-4 text-[#993322]"/>Wishlist</div>
          <div className="flex flex-col items-center gap-1"><MapPin className="w-4 h-4 text-[#2E4A35]"/>Saved addresses</div>
        </div>
      </div>
    </div>
  );

  const totalSpent = orders.reduce((s,o)=>s+(o.total||0), 0);

  return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 40, right: -180 }}/>
      <div className="blob blob-sage" style={{ width: 400, height: 400, top: 400, left: -140 }}/>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 relative z-10">
        <nav className="flex items-center gap-2 text-xs text-stone-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#3B2F2F]">Home</Link>
          <ChevronRight className="w-3 h-3"/>
          <span className="text-stone-800">My account</span>
        </nav>

        {/* Hero panel */}
        <div className="bg-gradient-to-br from-[#3B2F2F] to-[#2A2020] text-[#FDFBF7] rounded-3xl p-8 md:p-10 relative overflow-hidden shadow-2xl shadow-[#3B2F2F]/20">
          <div className="pattern-beans absolute inset-0 opacity-30"/>
          <div className="blob blob-gold" style={{ width: 300, height: 300, bottom: -80, right: -80, opacity:0.4 }}/>
          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-[#D4AF37]/60"/>
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center font-serif-display text-3xl text-[#3B2F2F]">{user.name?.[0] || "☕"}</div>
              )}
              <div>
                <div className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37]">Your account</div>
                <h1 className="font-serif-display text-4xl md:text-5xl leading-tight">Hello, {user.name?.split(" ")[0] || "friend"}</h1>
                <div className="text-xs text-[#FDFBF7]/60 mt-1">{user.email}</div>
              </div>
            </div>
            <div className="flex gap-2">
              {user.is_admin && (
                <Link to="/admin" data-testid="go-to-admin"
                      className="bg-[#D4AF37] text-[#3B2F2F] rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#E8C87C] transition-colors inline-flex items-center gap-2">
                  Admin Dashboard <ArrowRight className="w-3.5 h-3.5"/>
                </Link>
              )}
              <button onClick={logout} data-testid="account-logout"
                      className="border border-white/20 rounded-full px-4 py-2 text-sm hover:bg-white/10">
                Sign out
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8 relative">
            <StatBox label="Orders" value={orders.length}/>
            <StatBox label="Lifetime spend" value={inr(totalSpent)}/>
            <StatBox label="Since" value={new Date(user.created_at || Date.now()).toLocaleDateString(undefined,{month:"short",year:"numeric"})}/>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <a href="#orders"><QuickCard icon={Package} label="Track orders" sub={`${orders.length} in history`} color="#D4AF37" bg="#FEF3E2"/></a>
          <a href="#wishlist"><QuickCard icon={Heart} label="Wishlist" sub={`${wishProducts.length} saved`} color="#993322" bg="#F5DEC7"/></a>
          <QuickCard icon={MapPin} label="Addresses" sub="Manage delivery" color="#2E4A35" bg="#E8EDDF"/>
        </div>

        {/* Orders */}
        <div className="mt-10" id="orders">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-1">Order history</div>
              <h2 className="font-serif-display text-4xl">Recent orders</h2>
            </div>
            {orders.length > 0 && <div className="text-xs text-stone-500">{orders.length} total</div>}
          </div>

          {orders.length === 0 && (
            <div className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#F5F0E6] mx-auto flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-[#8B7355]"/>
              </div>
              <div className="font-serif-display text-2xl mb-2">No orders yet</div>
              <p className="text-stone-500 text-sm mb-6">Your first cup awaits.</p>
              <Link to="/shop" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 hover:bg-black text-sm">
                Start shopping <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          )}

          <div className="space-y-3">
            {orders.map(o => {
              const style = STATUS_STYLE[o.status] || {bg:"#F5F0E6", color:"#3B2F2F", label:o.status};
              return (
                <div key={o.order_id} data-testid={`order-${o.order_no}`}
                     onClick={() => navigate(`/order/${o.order_id}`)}
                     className="group cursor-pointer flex items-center gap-4 bg-white/80 backdrop-blur border border-stone-200 rounded-2xl p-4 hover:border-[#D4AF37]/60 hover:shadow-lg transition-all">
                  <div className="flex -space-x-3">
                    {o.items.slice(0,3).map((it,i)=>(
                      <img key={i} src={it.image} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white"/>
                    ))}
                    {o.items.length > 3 && (
                      <div className="w-12 h-12 rounded-full bg-[#F5F0E6] border-2 border-white flex items-center justify-center text-xs font-outfit font-bold text-[#3B2F2F]">+{o.items.length - 3}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-outfit font-bold text-[#3B2F2F]">{o.order_no}</div>
                      <span className="text-[10px] font-outfit font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{background:style.bg, color:style.color}}>
                        {style.label}
                      </span>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{new Date(o.created_at).toLocaleDateString(undefined,{day:"numeric",month:"short",year:"numeric"})} · {o.items.length} item{o.items.length!==1?"s":""}</div>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/invoice.pdf`}
                       target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       data-testid={`invoice-${o.order_no}`}
                       title="Download PDF invoice"
                       className="w-9 h-9 rounded-full border border-stone-200 flex items-center justify-center text-stone-500 hover:text-[#3B2F2F] hover:border-[#3B2F2F] hover:bg-white transition-colors">
                      <FileDown className="w-4 h-4"/>
                    </a>
                    <div>
                      <div className="font-outfit font-bold text-lg">{inr(o.total)}</div>
                      <div className="text-xs text-[#8B7355] group-hover:text-[#3B2F2F] flex items-center justify-end gap-1">View <ChevronRight className="w-3 h-3"/></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Wishlist */}
        <div className="mt-14" id="wishlist">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-1 flex items-center gap-2">
                <Heart className="w-3 h-3 fill-[#993322] text-[#993322]"/> Saved for later
              </div>
              <h2 className="font-serif-display text-4xl">Your wishlist</h2>
            </div>
            {wishProducts.length > 0 && <div className="text-xs text-stone-500">{wishProducts.length} item{wishProducts.length!==1?"s":""}</div>}
          </div>

          {wishProducts.length === 0 ? (
            <div className="bg-white/80 backdrop-blur border border-stone-200 rounded-3xl p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#F5DEC7] mx-auto flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 text-[#993322]"/>
              </div>
              <div className="font-serif-display text-2xl mb-2">Nothing saved yet</div>
              <p className="text-stone-500 text-sm mb-6">Tap the heart on any product to save it here.</p>
              <Link to="/shop" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 hover:bg-black text-sm">
                Browse products <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <WishlistShare hasItems={wishProducts.length > 0}/>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {wishProducts.map(p => (
                <div key={p.product_id} className="bg-white border border-stone-100 rounded-2xl overflow-hidden group hover:border-[#D4AF37]/40 hover:shadow-lg transition-all" data-testid={`wishlist-item-${p.slug}`}>
                  <Link to={`/product/${p.slug}`} className="block">
                    <div className="aspect-[4/3] overflow-hidden bg-[#F5F0E6]">
                      <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                    </div>
                  </Link>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{p.category_slug?.replace(/-/g," ")}</div>
                    <Link to={`/product/${p.slug}`} className="font-serif-display text-lg mt-1 leading-tight block hover:text-[#3B2F2F]">{p.name}</Link>
                    <div className="mt-2 flex items-center gap-1 text-xs text-stone-500">
                      <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]"/><span>{p.rating}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="font-outfit font-bold text-[#3B2F2F]">{inr(p.min_price)}</div>
                      <button onClick={()=>{ toggleWish(p.product_id); setWishProducts(prev=>prev.filter(x=>x.product_id!==p.product_id)); }}
                              data-testid={`wishlist-remove-${p.slug}`}
                              className="text-stone-400 hover:text-[#993322] p-1" title="Remove from wishlist">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{label}</div>
      <div className="font-outfit font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}

function QuickCard({ icon: Icon, label, sub, color, bg }) {
  return (
    <div className="bg-white border border-stone-100 rounded-2xl p-5 flex items-center gap-4 hover:shadow-lg transition-shadow">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{background: bg}}>
        <Icon className="w-6 h-6" style={{color}}/>
      </div>
      <div>
        <div className="font-outfit font-semibold">{label}</div>
        <div className="text-xs text-stone-500">{sub}</div>
      </div>
    </div>
  );
}
